import styled from 'styled-components';
import { Box, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { GridLayout, MoneyBackGuarantee } from '@99designs/marketing-ui';
import { FAQSection } from '../FAQSection';
import { formatFaqsFromContentful } from '../FAQSection/FAQFormatter';
import { Inspiration } from '../Inspiration';
import { ProductComparison } from '../ProductComparison';
import CustomerRatingsList from '../Rating/CustomerRatingList';
import { RelatedProducts } from '../RelatedProducts';
import { TabId } from '../TabsMenu';
import type { Design } from '@99designs/feature-design-inspiration';
import type { ZendeskLanguage } from '@99designs/routing';
import type { ComparativeProduct, RelatedProduct } from '../../types';
import type { Faq as ContentfulFaq } from '../../types/Contentful';

export interface InspirationProps {
    categoryKeys: string[];
    designOutcome: string;
    designs: Array<Design>;
}

interface Props {
    stickyColumnPosition?: 'left' | 'right';
    leftColumn: React.ReactNode;
    rightColumn: React.ReactNode;
    comparativeProducts?: ComparativeProduct[];
    inspiration?: InspirationProps;
    relatedProducts?: RelatedProduct[];
    showRating?: boolean;
    moneyBackGuarantee?: boolean;
    faqs?: ContentfulFaq[];
    lang?: ZendeskLanguage;
    productNameForTracking?: string;
    productIdForTracking?: string;
}

const TwoColumnLayout = styled(Box)`
    display: flex;
    gap: ${spaces['4bu']};

    ${mediaQuery.between('small', 'medium')} {
        flex-direction: column;
        gap: 0;
    }
`;

const StickyColumn = styled(Box)`
    height: fit-content;

    ${mediaQuery.minimum('normal')} {
        position: sticky;
        top: ${spaces['1.5bu']};
    }
`;

export function BaseLayout({
    stickyColumnPosition = 'left',
    leftColumn,
    rightColumn,
    comparativeProducts,
    inspiration,
    relatedProducts,
    moneyBackGuarantee = false,
    faqs,
    showRating = false,
    lang,
    productNameForTracking,
    productIdForTracking,
}: Props) {
    return (
        <Box paddingBottom={['2bu', '6bu', '6bu', '6bu']}>
            <Stack space={['4bu', '6bu', '6bu', '6bu']}>
                <GridLayout>
                    <TwoColumnLayout>
                        {stickyColumnPosition === 'left' && (
                            <>
                                <StickyColumn width={['100%', '100%', '50%', '50%']}>
                                    {leftColumn}
                                </StickyColumn>
                                <Box
                                    width={['100%', '100%', '50%', '50%']}
                                    paddingTop={['2bu', '2bu', '3bu', '3bu']}
                                >
                                    <Stack space={['2bu', '4bu', '4bu', '4bu']}>
                                        {rightColumn}
                                    </Stack>
                                    {moneyBackGuarantee && lang && (
                                        <Box
                                            paddingTop={['2bu', '1.5bu', '1.5bu', '1.5bu']}
                                            paddingBottom={['2bu', 'none', 'none', 'none']}
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <MoneyBackGuarantee lang={lang} />
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}
                        {stickyColumnPosition === 'right' && (
                            <>
                                <Box width={['100%', '100%', '60%', '60%']}>{leftColumn}</Box>
                                <StickyColumn width={['100%', '100%', '40%', '40%']}>
                                    {rightColumn}
                                </StickyColumn>
                            </>
                        )}
                    </TwoColumnLayout>
                </GridLayout>

                {comparativeProducts && comparativeProducts.length > 0 && (
                    <Box id={TabId.ProductComparison}>
                        <ProductComparison products={comparativeProducts} />
                    </Box>
                )}

                {inspiration && (
                    <Inspiration
                        {...inspiration}
                        productNameForTracking={productNameForTracking}
                        productIdForTracking={productIdForTracking}
                    />
                )}

                {showRating && <CustomerRatingsList />}

                {faqs && faqs.length > 0 && <FAQSection faqs={formatFaqsFromContentful(faqs)} />}

                {relatedProducts && relatedProducts.length > 0 && (
                    <RelatedProducts products={relatedProducts} />
                )}
            </Stack>
        </Box>
    );
}
